import { OrgHourlyRateMarkupType } from '../../common/CommonInterfaces';

const MASTER_WORKER_PAY_RATE_OVERRIDE_LABEL: string = 'Master Worker Pay Rate Override';
const ADD_CUSTOM_CLIENT_BILLING_RATES_BUTTON_LABEL: string = 'Add Custom Client Billing Rates';
const ADD_CLIENT_BILLING_RATES_OVERRIDE_BUTTON_LABEL: string = 'Add Client Billing Rates Override';
const WORKER_TYPE_EDIT_TAX_MENU_LABEL: string = 'Edit tax';
const WORKER_TYPE_EDIT_DISPLAY_NAME_MENU_LABEL: string = 'Display name';
const WORKER_TYPE_EDIT_PAY_AND_BILLING_RATES_MENU_LABEL: string = 'Pay and billing rates';
const WORKER_TYPE_EDIT_REMOVE_MENU_LABEL: string = 'Remove';
const WORKER_TYPE_NAME_LABEL: string = 'Worker Type';
const DEFAULT_JOBBER_PAY_RATE_LABEL: string = 'Default Jobber Pay Rate';
const RATE_MARKUP_TYPE_LABEL: string = 'Rate Markup Type';
const DEFAULT_ORG_BILLING_PAY_RATE_LABEL: string = 'Default Org Billing Pay Rate';
const DEFAULT_ORG_BILLING_MARKUP_PERCENTAGE_RATE_LABEL: string =
  'Default Org Billing Markup Percentage Rate';
const CLIENT_BILLING_WORKER_TYPE_NAME_LABEL: string = 'Client Billing Worker Type Name';
const WORKER_TYPE_DESCRIPTION_LABEL: string = 'Description';
const BILLING_BASE_RATE_LABEL: string = 'Billing Base Rate';
const HOURLY_CLIENT_BILLING_RATE_LABEL: string = 'Hourly Client Billing Rate';

const ADD_WORKER_TYPE_FORM_LEGEND: string =
  'Fields marked * must be filled in. Fields labeled Default are already set — you can leave them as-is.';

const RATE_MARKUP_TYPE_HELPER_TEXT: string =
  'Percent Markup: increase the client billing rate by a percentage of worker pay. Default Hourly Markup: bill the client a set hourly rate, no matter what the worker is paid.';

const getMarkupTypeTooltipText = (markupType: OrgHourlyRateMarkupType): string => {
  switch (markupType) {
    case OrgHourlyRateMarkupType.PERCENTAGE_RATE_MARKUP_TYPE:
      return 'Client invoices add this percent on top of worker pay for this role. You can still override the rate for a specific client.';
    case OrgHourlyRateMarkupType.DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE:
      return 'Client invoices use this fixed hourly rate for this role, no matter what the worker is paid. You can still override the rate for a specific client.';
    case OrgHourlyRateMarkupType.HOURLY_RATE_MARKUP_TYPE:
      return 'Client invoices add this dollar amount per hour on top of worker pay for this role. You can still override the rate for a specific client.';
    default:
      return RATE_MARKUP_TYPE_HELPER_TEXT;
  }
};

const CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS: Readonly<{
  readonly masterWorkerPayRateOverride: string;
  readonly addCustomClientBillingRates: string;
  readonly addClientBillingRatesOverride: string;
  readonly editTax: string;
  readonly editDisplayName: string;
  readonly editPayAndBillingRates: string;
  readonly removeOrgWorkerType: string;
  readonly workerTypeName: string;
  readonly defaultJobberPayRate: string;
  readonly rateMarkupType: string;
  readonly defaultOrgBillingPayRate: string;
  readonly defaultOrgBillingMarkupPercentageRate: string;
  readonly clientBillingWorkerTypeName: string;
  readonly description: string;
  readonly billingBaseRate: string;
  readonly hourlyClientBillingRate: string;
  readonly getMarkupTypeTooltipText: (markupType: OrgHourlyRateMarkupType) => string;
}> = {
  masterWorkerPayRateOverride:
    'Sets the hourly pay rate for every worker with this role at this client. Worker-specific overrides below take priority. Leave blank to use each worker’s usual pay rate instead.',
  addCustomClientBillingRates:
    'Add hourly billing rates for a role at this client, then set whether invoices for that role charge sales tax.',
  addClientBillingRatesOverride:
    'Set different billing or pay rates for one worker at this client. This overrides the usual rates for that worker only.',
  editTax:
    'Set whether this worker type charges sales tax for this client, and the tax rate for Ontario, British Columbia, or Alberta. Empty settings fall back to the agency worker type, then the usual tax rate.',
  editDisplayName:
    'Change the name shown on client invoices for this worker type. Leave unset to use the usual worker type name.',
  editPayAndBillingRates:
    'Update the worker pay and client billing rates used for this role at this client.',
  removeOrgWorkerType:
    'Remove this role’s custom billing rates from this client. Agency defaults will be used instead where needed.',
  workerTypeName:
    'The role name used when assigning workers and setting default pay and billing for this type of work.',
  defaultJobberPayRate:
    'The default hourly pay for workers in this role. You can still change pay for an individual worker.',
  rateMarkupType: RATE_MARKUP_TYPE_HELPER_TEXT,
  defaultOrgBillingPayRate:
    'The default hourly amount the client is billed for this role. You can still override this for a specific client.',
  defaultOrgBillingMarkupPercentageRate:
    'The percent added on top of worker pay when invoicing the client for this role. You can still override this for a specific client.',
  clientBillingWorkerTypeName:
    'The name shown on client invoices for this worker type. Leave unset to use the usual worker type name.',
  description:
    'A short note about this worker type for your team. Clients do not see this on invoices unless you use it elsewhere.',
  billingBaseRate:
    'The usual worker pay rate used as the starting point for this role at this client when no master pay override is set.',
  hourlyClientBillingRate:
    'The hourly amount this client is billed for this role on invoices.',
  getMarkupTypeTooltipText,
};

export {
  ADD_CLIENT_BILLING_RATES_OVERRIDE_BUTTON_LABEL,
  ADD_CUSTOM_CLIENT_BILLING_RATES_BUTTON_LABEL,
  ADD_WORKER_TYPE_FORM_LEGEND,
  BILLING_BASE_RATE_LABEL,
  CLIENT_BILLING_WORKER_TYPE_NAME_LABEL,
  CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS,
  DEFAULT_JOBBER_PAY_RATE_LABEL,
  DEFAULT_ORG_BILLING_MARKUP_PERCENTAGE_RATE_LABEL,
  DEFAULT_ORG_BILLING_PAY_RATE_LABEL,
  HOURLY_CLIENT_BILLING_RATE_LABEL,
  MASTER_WORKER_PAY_RATE_OVERRIDE_LABEL,
  RATE_MARKUP_TYPE_HELPER_TEXT,
  RATE_MARKUP_TYPE_LABEL,
  WORKER_TYPE_DESCRIPTION_LABEL,
  WORKER_TYPE_EDIT_DISPLAY_NAME_MENU_LABEL,
  WORKER_TYPE_EDIT_PAY_AND_BILLING_RATES_MENU_LABEL,
  WORKER_TYPE_EDIT_REMOVE_MENU_LABEL,
  WORKER_TYPE_EDIT_TAX_MENU_LABEL,
  WORKER_TYPE_NAME_LABEL,
};
